import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { Tab } from 'src/app/components/tabs/tabs.component';
import { AlertType, OptionType, StepState } from 'src/app/core/enums/enums';
import { DOCSADoc, DOCSARequest } from 'src/app/core/models/docsa.model';
import { ExchangeModuleFilter } from 'src/app/core/models/exchange-filter.model';
import { FareFamiliesState } from 'src/app/core/models/fare-family.model';
import { ContractError } from 'src/app/core/models/models';
import { ModuleSteps, Step } from 'src/app/core/models/step.model';
import { InvoluntaryChangeActions as actions } from '../actions';
import { ExceptionActions } from 'src/app/exchange-override/actions';
import { ExchangeOverrideState } from 'src/app/core/models/exchange-override.model';
import { EmergencyContactModel } from 'src/app/core/models/emergency-contact.model';
import { IClientSettings } from 'src/app/core/models/agency-settings.model';
import { selectPnrState } from 'src/app/search-result/reducers/pnr.reducer';
import { selectSettingsState } from 'src/app/settings/reducer/settings.reducer';
import { SplitPnrStateReset } from '../actions/involuntary-change.actions';
import {
  Itinerary,
  Passenger,
  PnrInfo,
  QuoteRequest,
  Segment
} from 'src/app/core/models/itinerary.model';
import {
  airlineOptionSteps,
  airlineOptionWithSplitSteps,
  alternativeSteps,
  alternativeStepsNewFlow,
  alternativeStepsNewFlowWithSplitStep,
  alternativeWithsplitSteps,
  stepsNewFlowWithReissue
} from './involuntary-steps';
import {
  ExchangeOptionFilter,
  IAncillariesWaiveState,
  IInvoluntaryBookStepStates,
  InvoluntaryExchangeQuote
} from 'src/app/core/models/involuntary-change.model';
import {
  ExchangeAvailability,
  ExchangeAvailabilityOption,
  ExchangeQuoteRequest,
  ExchangeSegment
} from 'src/app/core/models/voluntary-change.model';
import {
  SplitPnrFail,
  SplitPnrRequest,
  SplitPnrSuccess
} from 'src/app/search-result/actions/pnr.actions';

export interface InvoluntaryChangeState {
  isAirlineOption: boolean;
  quoteRequest: QuoteRequest;
  involuntaryQuote: InvoluntaryExchangeQuote;
  loading: boolean;
  selectedOption: ExchangeAvailabilityOption;
  exchangeSegments: ExchangeSegment[];
  availabilities: ExchangeAvailability;
  originalItinerary?: Itinerary;
  availabilitiesPerSegment: ExchangeAvailability[];
  filtersPerSegment: ExchangeOptionFilter[];
  availabilitiesState: AlertType;
  availabilitiesError: ContractError;
  availabilitiesTabs: Tab[];
  finalError: ContractError;
  newPnr: PnrInfo;
  finalState: AlertType;
  activeStep: number;
  steps: Step[];
  activeTab: number;
  tabs: Tab[];
  hasSplit: boolean;
  splitState: AlertType;
  splitError: ContractError;
  splittedPnr: PnrInfo;
  isReIssueRequired: boolean;
  emergencyContact: EmergencyContactModel[];
  docsaDocs: DOCSADoc[];
  needsDocsaRequest: boolean;
  docsaFail: boolean;
  exchangePricing: ExchangeAvailabilityOption;
  fareFamiliesState: FareFamiliesState;
  index: number;
  finalRequest: ExchangeQuoteRequest;
  commission: number;
  pricingError: ContractError;
  exchangeOverrideState: ExchangeOverrideState;
  isNewFlow: boolean;
  exchangeOriginalRoute: boolean;
  showAncillariesModal: boolean;
  ancillariesError: ContractError;
  passengersWithAncillaryValidation: Passenger[][];
  settings: IClientSettings;
  ancillariesWaiveState: IAncillariesWaiveState;
  involuntaryBookStepStates: IInvoluntaryBookStepStates[];
  reloadAvailabilities: boolean;
}

export const initialState: InvoluntaryChangeState = {
  isAirlineOption: null,
  quoteRequest: {} as QuoteRequest,
  involuntaryQuote: null,
  loading: false,
  selectedOption: null,
  exchangeSegments: [],
  availabilities: null,
  originalItinerary: null,
  availabilitiesPerSegment: [],
  availabilitiesState: null,
  availabilitiesError: null,
  availabilitiesTabs: [],
  filtersPerSegment: [],
  finalError: null,
  newPnr: null,
  finalState: null,
  activeStep: 1,
  steps: alternativeSteps.steps,
  activeTab: 0,
  tabs: [{ name: 'voluntary-change.search', hide: false }, { name: 'voluntary-change.passengers' }],
  hasSplit: false,
  splitState: AlertType.Warning,
  splitError: null,
  splittedPnr: null,
  isReIssueRequired: true,
  emergencyContact: [],
  docsaDocs: [],
  needsDocsaRequest: false,
  docsaFail: false,
  exchangePricing: null,
  fareFamiliesState: null,
  index: null,
  finalRequest: null,
  commission: null,
  pricingError: null,
  exchangeOverrideState: null,
  isNewFlow: false,
  exchangeOriginalRoute: false,
  showAncillariesModal: false,
  ancillariesError: null,
  passengersWithAncillaryValidation: [],
  settings: null,
  reloadAvailabilities: false,
  ancillariesWaiveState: {
    isCompleted: false,
    isRequired: false,
    error: {
      hasError: false,
      error: null
    }
  },
  involuntaryBookStepStates: [
    {
      title: 'involuntary-change.steps.book-step.involuntary-book',
      state: StepState.Normal
    },
    {
      title: 'involuntary-change.steps.book-step.involuntary-finish-book',
      state: StepState.Normal
    }
  ]
};

/** Other Alternative         ** Airline Alternative
 * 1 -  Choose Alternative    ** 1. Split if required
 * 2 -  Split if required     ** 2. Final
 * 3 -  Final confirmation    **
 *
 * 4 - Seat Map
 */

const involuntaryChangeReducer = createReducer(
  initialState,

  on(actions.clearInvoluntaryChange, (state) => ({
    isAirlineOption: null,
    quoteRequest: {} as QuoteRequest,
    involuntaryQuote: null,
    loading: false,
    selectedOption: null,
    exchangeSegments: null,
    availabilities: null,
    originalItinerary: null,
    availabilitiesPerSegment: [],
    availabilitiesState: null,
    availabilitiesError: null,
    availabilitiesTabs: null,
    filtersPerSegment: [],
    finalError: null,
    newPnr: null,
    finalState: null,
    activeStep: 1,
    steps: alternativeSteps.steps,
    activeTab: 0,
    tabs: [
      { name: 'voluntary-change.search', hide: false },
      { name: 'voluntary-change.passengers' }
    ],
    hasSplit: false,
    splitState: AlertType.Warning,
    splitError: null,
    splittedPnr: null,
    isReIssueRequired: true,
    emergencyContact: [],
    docsaDocs: [],
    needsDocsaRequest: false,
    docsaFail: false,
    exchangePricing: null,
    fareFamiliesState: null,
    index: null,
    finalRequest: null,
    commission: null,
    pricingError: null,
    exchangeOverrideState: null,
    isNewFlow: false,
    exchangeOriginalRoute: false,
    showAncillariesModal: false,
    ancillariesError: null,
    passengersWithAncillaryValidation: null,
    settings: null,
    reloadAvailabilities: false,
    ancillariesWaiveState: {
      isCompleted: false,
      isRequired: false,
      error: {
        hasError: false,
        error: null
      }
    },
    involuntaryBookStepStates: [
      {
        title: 'involuntary-change.steps.book-step.involuntary-book',
        state: StepState.Normal
      },
      {
        title: 'involuntary-change.steps.book-step.involuntary-finish-book',
        state: StepState.Normal
      }
    ]
  })),

  on(actions.LoadInvoluntaryQuoteSuccess, (state, { quote, request, hasSplit, settings }) => ({
    ...initialState,
    exchangePricing: state.exchangePricing,
    involuntaryQuote: quote,
    loading: false,
    quoteRequest: request,
    hasSplit,
    originalItinerary: quote.originalItinerary,
    settings,
    steps: hasSplit
      ? alternativeWithsplitSteps.active(0)
      : alternativeSteps.deactivate(1, 2) && alternativeSteps.active(0)
  })),

  on(actions.ChooseArlineOption, (state, { waiverData }) => ({
    ...state,
    quoteRequest: { ...state.quoteRequest, ...waiverData },
    isAirlineOption: true,
    tabs: [
      { name: 'voluntary-change.search', hide: true },
      { name: 'voluntary-change.passengers' }
    ],
    activeTab: 1,
    loading: false,
    activeStep: state.hasSplit ? 1 : 2,
    steps: state.hasSplit ? airlineOptionWithSplitSteps.active(1) : airlineOptionSteps.active(1)
  })),

  on(actions.ChangeActiveTab, (state, { activeTab }) => ({
    ...state,
    activeTab,
    loading: false
  })),

  on(actions.ChooseOtherAlternative, (state) => ({
    ...state,
    loading: false,
    selectedOption: {
      ...state.availabilitiesPerSegment[0]?.selectedOption,
      segments: state.availabilitiesPerSegment.reduce(
        (fin, availability) => fin.concat(availability.selectedOption?.segments),
        []
      )
    },
    isAirlineOption: false,
    tabs: [
      { name: 'involuntary-change.search', hide: true },
      { name: 'involuntary-change.passengers' }
    ],
    activeTab: 2,
    activeStep: state.hasSplit ? 2 : 3,
    steps: state.hasSplit
      ? (alternativeWithsplitSteps.active(1), alternativeWithsplitSteps.deactivate(5, 1))
      : alternativeSteps.deactivate(2, 0) && alternativeSteps.active(3)
  })),

  on(actions.goToSeatmapNewFlow, (state) => ({
    ...state,
    activeStep: 5,
    steps: state.hasSplit
      ? alternativeStepsNewFlowWithSplitStep.active(3)
      : new ModuleSteps(state.steps).active(2)
  })),

  on(actions.ChooseOtherAlternativeFromNewFlow, (state) => ({
    ...state,
    selectedOption: {
      ...state.availabilitiesPerSegment[0]?.selectedOption,
      segments: state.availabilitiesPerSegment.reduce(
        (fin, availability) => fin.concat(availability.selectedOption?.segments),
        []
      )
    },
    isAirlineOption: false,
    tabs: [
      { name: 'involuntary-change.search', hide: true },
      { name: 'involuntary-change.passengers' }
    ],
    activeTab: 2,
    pricingError: null,
    loading: false,
    activeStep: state.hasSplit ? 2 : 3,
    steps: new ModuleSteps(new ModuleSteps(state.steps).removeStep(3)).active(2)
  })),

  on(actions.ChooseExchangePricing, (state) => ({
    ...state,
    activeStep: 7,
    pricingError: null,
    loading: false,
    steps: state.settings?.reissue?.isInvoluntary
      ? stepsNewFlowWithReissue.active(2)
      : alternativeStepsNewFlow.active(3) &&
        alternativeStepsNewFlow.stateOnActive(StepState.Active),
    selectedOption: {
      ...state.availabilitiesPerSegment[0].selectedOption,
      segments: state.availabilitiesPerSegment.reduce(
        (fin, availability) => fin.concat(availability.selectedOption?.segments),
        []
      )
    }
  })),

  on(actions.BackToAlternatives, (state) => ({
    ...state,
    activeStep: 1,
    steps: state.hasSplit
      ? alternativeWithsplitSteps.active(0)
      : alternativeSteps.deactivate(1, 2) && alternativeSteps.active(0),
    selectedOption: null,
    activeTab: 0,
    availabilitiesError: null,
    finalError: null,
    finalState: null,
    loading: false,
    tabs: initialState.tabs
  })),

  on(actions.BackToNewFlowStep, (state) => ({
    ...state,
    activeStep: 1,
    steps: alternativeStepsNewFlow.active(0),
    selectedOption: null,
    activeTab: 0,
    loading: false,
    availabilitiesError: null,
    finalError: null,
    finalState: null,
    tabs: initialState.tabs
  })),

  on(actions.BackToAlternativesNewFlowStep, (state) => ({
    ...state,
    activeStep: 1,
    steps: alternativeStepsNewFlow.active(0),
    selectedOption: null,
    availabilitiesPerSegment: null,
    activeTab: 0,
    availabilitiesError: null,
    finalError: null,
    finalState: null,
    pricingError: null,
    loading: false,
    tabs: initialState.tabs,
    reloadAvailabilities: true
  })),

  on(actions.LoadAvailabilitiesInvol, (state, { exchangeSegments }) => ({
    ...state,
    filter: null,
    activeStep: 1,
    exchangeSegments
  })),

  on(actions.LoadingStart, (state) => ({
    ...state,
    loading: true
  })),

  on(actions.LoadAvailabilitesPerSegmentSuccess, (state, { availabilitiesPerSegment }) => ({
    ...state,
    availabilitiesPerSegment,
    loading: false,
    reloadAvailabilities: false,
    exchangeOriginalRoute: availabilitiesPerSegment
      .map((availabiliy) => availabiliy.exchangeRoute)
      .reduce((acc, value) => value || acc),
    filtersPerSegment: availabilitiesPerSegment.map((a) => null)
  })),

  on(actions.LoadAvailabilitesTabs, (state, { availabilitiesTabs }) => ({
    ...state,
    availabilitiesTabs,
    loading: false
  })),

  on(actions.AddSelectedOption, (state, { selectedOption, index }) => {
    const availabilities = state.availabilitiesPerSegment.map((availability, i) => {
      return index === i ? { ...availability, selectedOption } : availability;
    });

    return {
      ...state,
      exchangePricing: selectedOption,
      availabilitiesPerSegment: availabilities,
      loading: false
    };
  }),

  on(actions.LoadAvailabilitesSuccess, (state, { availabilities }) => ({
    ...state,
    availabilities,
    loading: false,
    availabilitiesState: availabilities.options.length === 0 ? AlertType.Info : null
  })),

  on(actions.LoadAvailabilitesError, (state, { error }) => ({
    ...state,
    loading: false,
    reloadAvailabilities: false,
    availabilitiesError: error,
    availabilitiesState: AlertType.Error
  })),

  on(actions.loadingStop, (state) => ({
    ...state,
    loading: false
  })),

  on(SplitPnrRequest, (state) => ({
    ...state,
    loading: true
  })),

  on(SplitPnrStateReset, (state) => ({
    ...state,
    splitState: AlertType.Warning,
    splittedPnr: null,
    splitError: null,
    loading: false
  })),

  on(SplitPnrFail, (state, { splitError }) => ({
    ...state,
    splitError,
    loading: false,
    splitState: AlertType.Error,
    steps: state.isNewFlow
      ? alternativeStepsNewFlowWithSplitStep.stateOnActive(StepState.Error)
      : state.isAirlineOption
      ? airlineOptionWithSplitSteps.stateOnActive(StepState.Error)
      : alternativeWithsplitSteps.stateOnActive(StepState.Error)
  })),

  on(SplitPnrSuccess, (state, { pnr }) => ({
    ...state,
    quoteRequest: { ...state.quoteRequest, pnr: pnr.code },
    newPnr: {
      ...state.newPnr,
      passengers: pnr?.passengers
    },
    splittedPnr: pnr,
    hasSplit: false,
    steps: state.isNewFlow
      ? state.steps
      : state.isAirlineOption
      ? airlineOptionWithSplitSteps.stateOnActive(StepState.Completed)
      : alternativeWithsplitSteps.stateOnActive(StepState.Completed),
    splitState: AlertType.Success,
    loading: false
  })),

  on(actions.EndSplit, (state) => ({
    ...state,
    activeStep: 2,
    loading: false,
    steps: state.isNewFlow
      ? state.steps
      : state.isAirlineOption
      ? airlineOptionWithSplitSteps.active(2)
      : alternativeWithsplitSteps.active(2)
  })),

  on(actions.RequestChangeAirlineRecommendation, (state) => ({
    ...state,
    loading: true
  })),
  on(actions.RequestChangeAirlineRecommendationSuccess, (state, { pnr }) => ({
    ...state,
    newPnr: pnr,
    finalState: AlertType.Success,
    loading: false,
    steps: state.hasSplit
      ? airlineOptionWithSplitSteps.stateOnActive(StepState.Completed)
      : airlineOptionSteps.stateOnActive(StepState.Completed)
  })),
  on(actions.RequestChangeAirlineRecommendationFail, (state, { error }) => ({
    ...state,
    finalError: error,
    finalState: AlertType.Error,
    loading: false,
    steps: state.hasSplit
      ? airlineOptionWithSplitSteps.stateOnActive(StepState.Error)
      : airlineOptionSteps.stateOnActive(StepState.Error)
  })),
  on(actions.ForceRefreshSesionAndBook, (state) => ({
    ...state,
    loading: true
  })),
  on(actions.InvoluntaryBookRequestAPI, (state) => ({
    ...state,
    loading: true
  })),
  on(actions.InvoluntaryBookSuccess, (state, { pnr }) => ({
    ...state,
    finalState: AlertType.Success,
    loading: false,
    newPnr: pnr,
    steps: state.hasSplit ? alternativeWithsplitSteps.complete(3) : alternativeSteps.complete(3),
    involuntaryBookStepStates: [
      {
        title: 'involuntary-change.steps.book-step.involuntary-book',
        state: StepState.Completed
      },
      {
        title: 'involuntary-change.steps.book-step.involuntary-finish-book',
        state: StepState.Completed
      }
    ]
  })),
  on(actions.InvoluntaryBookFail, (state, { error }) => ({
    ...state,
    finalState: AlertType.Error,
    finalError: error,
    loading: false,
    steps: state.hasSplit
      ? alternativeWithsplitSteps.stateOnActive(StepState.Error)
      : alternativeSteps.stateOnActive(StepState.Error),
    involuntaryBookStepStates: [
      {
        title: 'involuntary-change.steps.book-step.involuntary-book',
        state: StepState.Error
      },
      {
        title: 'involuntary-change.steps.book-step.involuntary-finish-book',
        state: StepState.Muted
      }
    ]
  })),
  on(actions.InvoluntaryFinishBookRequestFail, (state, { error }) => ({
    ...state,
    finalState: AlertType.Error,
    finalError: error,
    loading: false,
    steps: state.hasSplit
      ? alternativeWithsplitSteps.stateOnActive(StepState.Error)
      : alternativeSteps.stateOnActive(StepState.Error),
    involuntaryBookStepStates: [
      {
        title: 'involuntary-change.steps.book-step.involuntary-book',
        state: StepState.Completed
      },
      {
        title: 'involuntary-change.steps.book-step.involuntary-finish-book',
        state: StepState.Error
      }
    ]
  })),
  on(actions.ExchangeBookRequest, (state) => ({
    ...state,
    loading: true
  })),
  on(actions.ExchangeBookSuccess, (state, { pnr }) => ({
    ...state,
    finalState: AlertType.Success,
    loading: false,
    newPnr: pnr,
    steps: alternativeStepsNewFlow.stateOnActive(StepState.Completed)
  })),
  on(actions.ExchangeBookFail, (state, { error }) => ({
    ...state,
    finalState: AlertType.Error,
    finalError: error,
    loading: false,
    steps: alternativeStepsNewFlow.stateOnActive(StepState.Error)
  })),
  on(actions.Filter, (state, { filter, index }) => ({
    ...state,
    loading: false,
    filtersPerSegment: state.filtersPerSegment.map((current, i) =>
      i === index ? { ...current, ...filter } : current
    )
  })),
  on(actions.SetIsReIssueRequired, (state, { isReIssueRequired }) => ({
    ...state,
    isReIssueRequired: isReIssueRequired
  })),
  on(actions.EmergencyContact, (state, { contact }) => ({
    ...state,
    loading: false,
    emergencyContact: contact
  })),
  on(actions.DocsaSave, (state, { docs }) => ({
    ...state,
    loading: false,
    docsaDocs: docs
  })),
  on(actions.DocsaRequest, (state) => ({
    ...state
  })),

  // SeatMap
  on(actions.GoToSeatMap, (state) => ({
    ...state,
    activeStep: 4,
    loading: false,
    steps: state.hasSplit
      ? alternativeStepsNewFlowWithSplitStep.active(2)
      : alternativeStepsNewFlow.active(1)
  })),
  // End SeatMap

  on(actions.GoToSplitStepNewFlow, (state) => ({
    ...state,
    activeStep: 2,
    loading: false,
    steps: alternativeStepsNewFlowWithSplitStep.active(1)
  })),

  on(actions.goToEmergencyContact, (state) => ({
    ...state,
    activeStep: state.hasSplit ? 2 : 5,
    needsDocsaRequest: false,
    loading: false,
    steps: state.hasSplit
      ? alternativeWithsplitSteps.deactivate(0, 0) && alternativeWithsplitSteps.active(1)
      : alternativeSteps.deactivate(2, 0) && alternativeSteps.active(1)
  })),
  on(actions.goToAditionalInformation, (state) => ({
    ...state,
    activeStep: state.hasSplit ? 2 : 7,
    needsDocsaRequest: false,
    loading: false,
    steps: state.hasSplit
      ? alternativeWithsplitSteps.deactivate(0, 0) && alternativeWithsplitSteps.active(1)
      : alternativeSteps.deactivate(2, 0) && alternativeSteps.active(2)
  })),
  on(actions.GoToDocsa, (state) => ({
    ...state,
    activeStep: 5,
    needsDocsaRequest: true,
    loading: false,
    steps: state.hasSplit
      ? alternativeWithsplitSteps.deactivate(0, 0) && alternativeWithsplitSteps.active(1)
      : alternativeSteps.deactivate(3, 2) && alternativeSteps.active(2)
  })),
  on(actions.GoToConfirmation, (state) => ({
    ...state,
    activeStep: 2,
    loading: false,
    steps: state.hasSplit
      ? alternativeWithsplitSteps.deactivate(5, 5) && alternativeWithsplitSteps.active(2)
      : alternativeSteps.active(3)
  })),
  on(actions.ReturnToStep1, (state) => ({
    ...state,
    activeStep: 1,
    needsDocsaRequest: false,
    steps: state.hasSplit
      ? alternativeWithsplitSteps.deactivate(1, 1) && alternativeWithsplitSteps.active(0)
      : alternativeSteps.deactivate(1, 3) && alternativeSteps.active(0)
  })),
  on(actions.DocsaRequestSuccess, (state, { docsaConfirmation }) => ({
    ...state,
    loading: false,
    finalRequestState: AlertType.Success,
    steps: alternativeSteps.stateOnActive(StepState.Completed)
  })),
  on(actions.InvoluntaryChangeFail, (state, { involuntaryChangeError, docsaFail }) => ({
    ...state,
    loading: false,
    finalRequestState: AlertType.Error,
    finalRequestError: {
      ...involuntaryChangeError,
      errorCode: docsaFail ? 999 : involuntaryChangeError.errorCode || 10
    },
    docsaFail,
    steps: alternativeSteps.stateOnActive(StepState.Error)
  })),
  on(actions.MLSave, (state) => ({
    ...state,
    loading: true
  })),
  on(actions.ExchangeIASuccess, (state) => ({
    ...state,
    loading: false
  })),
  on(actions.ExchangeIAFail, (state) => ({
    ...state,
    loading: false
  })),
  on(actions.ExchangePricingRequest, (state) => ({
    ...state,
    loading: true
  })),
  on(actions.ExchangePricingSuccess, (state, { exchangePricing }) => ({
    ...state,
    activeStep: 7,
    loading: false,
    exchangePricing: exchangePricing,
    pricingError: null,
    steps: state.hasSplit
      ? alternativeStepsNewFlowWithSplitStep.active(4)
      : alternativeStepsNewFlow.active(3) && alternativeStepsNewFlow.stateOnActive(StepState.Active)
  })),
  on(actions.ExchangePricingWithoutAISuccess, (state, { exchangePricing, settings }) => ({
    ...state,
    loading: false,
    exchangePricing: exchangePricing,
    steps: settings.reissue.isInvoluntary
      ? stepsNewFlowWithReissue.active(1)
      : alternativeStepsNewFlow.active(1) && alternativeStepsNewFlow.stateOnActive(StepState.Active)
  })),
  on(actions.ExchangePricingFail, (state, { error }) => ({
    ...state,
    loading: false,
    pricingError: error,
    steps: alternativeStepsNewFlow.stateOnActive(StepState.Error)
  })),
  on(actions.ChangeStepsNewFlow, (state, { isReIssueActive }) => ({
    ...state,
    steps: isReIssueActive
      ? stepsNewFlowWithReissue.active(0)
      : state.hasSplit
      ? alternativeStepsNewFlowWithSplitStep.active(0)
      : alternativeStepsNewFlow.active(0)
  })),
  on(actions.SaveCommission, (state, { value }) => ({
    ...state,
    commission: value
  })),
  on(actions.LoadFareFamiliesInvoluntary, (state, { option, index, open }) => ({
    ...state,
    index,
    finalRequest: {
      ...state.finalRequest,
      selectedOption: option
    },
    fareFamiliesState: {
      error: null,
      loading: true
    },
    openDetailModal: open
  })),
  on(actions.LoadFareFamiliesSuccess, (state, { zones }) => ({
    ...state,
    fareFamiliesState: {
      zones,
      loading: false
    }
  })),
  on(actions.LoadFareFamiliesFail, (state, { error }) => ({
    ...state,
    fareFamiliesState: {
      error,
      loading: false
    }
  })),
  on(ExceptionActions.SaveFixedChargesNewFlow, (state, { fixedChargeOverrides }) => ({
    ...state,
    exchangePricing: {
      ...state.exchangePricing,
      exchangeFare: {
        ...state.exchangePricing.exchangeFare,
        exchangeFareOverride: {
          ...state.exchangePricing.exchangeFare.exchangeFareOverride,
          fixedChargeOverrides,
          fixedChargesState: StepState.Active
        }
      }
    },
    exchangeOverrideState: {
      ...state.exchangeOverrideState,
      changeFixedCharges: true,
      loading: true
    }
  })),
  on(ExceptionActions.SavePenaltiesNewFlow, (state, { penalties }) => ({
    ...state,
    exchangePricing: {
      ...state.exchangePricing,
      exchangeFare: {
        ...state.exchangePricing.exchangeFare,
        exchangeFareOverride: {
          ...state.exchangePricing.exchangeFare.exchangeFareOverride,
          penalties,
          penaltiesState: StepState.Active
        }
      }
    },
    exchangeOverrideState: {
      ...state.exchangeOverrideState,
      changePenalties: true,
      loading: true
    }
  })),
  on(ExceptionActions.ChangeCostModeNewFlow, (state, { isNoDifferenceValue }) => ({
    ...state,
    loading: true,
    exchangePricing: {
      ...state.exchangePricing,
      exchangeFare: {
        ...state.exchangePricing.exchangeFare,
        exchangeFareOverride: {
          ...state.exchangePricing.exchangeFare.exchangeFareOverride,
          isNoDifferenceValue
        }
      }
    },
    exchangeOverrideState: {
      ...state.exchangeOverrideState,
      changePenalties: false,
      changeFixedCharges: false,
      loading: true
    }
  })),
  on(ExceptionActions.ExchangeOverrideNewFlowSuccess, (state, { exchangeFare }) => ({
    ...state,
    loading: false,
    exchangePricing: {
      ...state.exchangePricing,
      exchangeFare: {
        ...exchangeFare,
        exchangeFareOverride: {
          ...exchangeFare.exchangeFareOverride,
          penaltiesState: state.exchangeOverrideState.changePenalties
            ? StepState.Completed
            : state.exchangePricing.exchangeFare.exchangeFareOverride.penaltiesState,
          fixedChargesState: state.exchangeOverrideState.changeFixedCharges
            ? StepState.Completed
            : state.exchangePricing.exchangeFare.exchangeFareOverride.fixedChargesState
        }
      }
    },
    exchangeOverrideState: {
      loading: false,
      error: null,
      changePenalties: false,
      changeFixedCharges: false
    }
  })),
  on(ExceptionActions.ExchangeOverrideNewFlowFail, (state, { error }) => ({
    ...state,
    loading: false,
    exchangeOverrideState: {
      loading: false,
      error,
      changePenalties: false,
      changeFixedCharges: false
    }
  })),
  on(actions.SaveIsNewFlow, (state, { value }) => ({
    ...state,
    isNewFlow: value
  })),
  on(actions.LoadAncillariesComparison, (state) => ({
    ...state,
    loading: true,
    ancillariesError: null,
    showAncillariesModal: false
  })),
  on(actions.LoadAncillariesComparisonSuccess, (state) => ({
    ...state,
    loading: false,
    showAncillariesModal: true
  })),
  on(actions.LoadAncillariesComparisonFail, (state, { error }) => ({
    ...state,
    loading: false,
    ancillariesError: error,
    showAncillariesModal: true
  })),
  on(actions.CloseAncillariesModal, (state) => ({
    ...state,
    showAncillariesModal: false
  })),
  on(actions.LoadAncillariesSegments, (state) => ({
    ...state,
    loading: true,
    ancillariesError: null,
    showAncillariesModal: false
  })),
  on(actions.LoadAncillariesSegmentsSuccess, (state, { passengers }) => ({
    ...state,
    loading: false,
    passengersWithAncillaryValidation: passengers,
    showAncillariesModal: true
  })),
  on(actions.LoadAncillariesSegmentsFail, (state, { error }) => ({
    ...state,
    loading: false,
    ancillariesError: error,
    showAncillariesModal: true
  })),
  on(actions.AncillariesWaive, (state) => ({
    ...state,
    loading: true
  })),
  on(actions.AncillariesWaiveRequired, (state) => ({
    ...state,
    ancillariesWaiveState: {
      ...state.ancillariesWaiveState,
      isRequired: true
    }
  })),
  on(actions.AncillariesWaiveSuccess, (state) => ({
    ...state,
    loading: false,
    ancillariesWaiveState: {
      ...state.ancillariesWaiveState,
      isCompleted: true
    }
  })),
  on(actions.AncillariesWaiveFail, (state, { error }) => ({
    ...state,
    loading: false,
    ancillariesWaiveState: {
      ...state.ancillariesWaiveState,
      isCompleted: false,
      error: {
        hasError: true,
        error
      }
    }
  })),
  on(actions.ResetAncillariesWaiveState, (state) => ({
    ...state,
    loading: false,
    ancillariesWaiveState: {
      isRequired: false,
      isCompleted: false,
      error: {
        hasError: false,
        error: null
      }
    }
  })),
  on(actions.UpdatePricingQuoteRequestWithSeatPrices, (state, { prices }) => ({
    ...state,
    finalRequest: {
      ...state.finalRequest,
      reservationSeatPrices: prices
    }
  }))
);

export function reducer(
  state: InvoluntaryChangeState | undefined,
  action: Action
): InvoluntaryChangeState {
  return involuntaryChangeReducer(state, action);
}

export const selectInvoluntaryChange = createFeatureSelector<InvoluntaryChangeState>(
  OptionType.InvoluntaryChange
);

export const selectQuoteRequest = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.quoteRequest
);

export const selectStepState = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => ({
    activeStep: state.activeStep,
    steps: state.steps
  })
);

export const selectInvoluntaryQuote = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.involuntaryQuote
);

export const selectCommission = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.commission
);

export const selectIsNewFlow = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.isNewFlow
);

export const selectLoading = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.loading
);

export const selectselectedOption = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.selectedOption
);

export const selectExchangePricingOption = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state?.exchangePricing
);

export const selectselectedOptionSegemnts = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.selectedOption?.segments
);

export const selectselectedOptionSaved = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.availabilitiesPerSegment
);

export const selectPerSegmentsSelected = createSelector(selectInvoluntaryChange, (state) => {
  let segments: Segment[] = [];
  if (state.availabilitiesPerSegment.length > 1) {
    state?.availabilitiesPerSegment?.map((availability) => {
      availability?.selectedOption?.segments.forEach((segment) => {
        segments.push(segment);
      });
    });
  } else {
    state.availabilitiesPerSegment[0]?.selectedOption?.segments.forEach((segment) => {
      segments.push(segment);
    });
  }

  return segments;
});

// ENABLE OPTIONS
export const getSelectedFlightIndexes = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) =>
    (state?.exchangeSegments || []).reduce(
      (fin, current) => (current.enabled ? fin.concat(current.flightIndexes) : fin),
      []
    )
);

// HERE
export const selectAvailabilitiesPerSegment = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.availabilitiesPerSegment
);

export const selectReservationSeatPrices = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.finalRequest?.reservationSeatPrices
);

export const exchangeSegments = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.exchangeSegments
);

export const selectOptionsPerSegment = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) =>
    !!state.availabilitiesPerSegment
      ? state.availabilitiesPerSegment.map((availability, index) => {
          const filter = state.filtersPerSegment[index];
          const options = availability.options;
          if (filter !== null && options !== null) {
            const filterM = new ExchangeModuleFilter(options, filter);
            return { ...availability, options: filterM.filterAvailabilityOptions() };
          }
          return availability;
        })
      : null
);

export const selectAvailabilitiesTabs = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.availabilitiesTabs
);

export const selectFinalState = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => ({
    data: state.newPnr,
    error: state.finalError,
    state: state.finalState
  })
);

export const selectAvailabilitiesState = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => ({
    data: state.availabilities,
    error: state.availabilitiesError,
    state: state.availabilitiesState
  })
);

const segmentHasAllClasses = (segments: Segment[]): boolean => {
  return segments.reduce(
    (allSegments, segment) =>
      segment.flights.reduce((allFlights, flight) => !!flight.classOfService && allFlights, true) &&
      allSegments,
    true
  );
};

export const getAllClassesSelected = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => {
    const aperSegment = state.availabilitiesPerSegment;
    return !!aperSegment
      ? aperSegment.reduce(
          (fin, current) =>
            fin && !!current.selectedOption
              ? segmentHasAllClasses(current.selectedOption?.segments)
              : false,
          true
        )
      : null;
  }
);

export const stateEmergencyContact = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => {
    const perSegment = state.availabilitiesPerSegment;
    return true;
  }
);

export const selectTabState = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => ({
    activeTab: state.activeTab,
    tabs: state.tabs
  })
);

export const selectNewSegments = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => {
    return state.isAirlineOption && state.involuntaryQuote
      ? state.involuntaryQuote.newItinerary?.segments
      : state.selectedOption
      ? state.selectedOption?.segments
      : [];
  }
);

export const selectSplitState = createSelector(selectInvoluntaryChange, (state) => ({
  state: state.splitState,
  data: state.splittedPnr,
  error: state.splitError
}));

export const selectPricingError = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.pricingError
);

export const getEmergencyContact = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.emergencyContact
);

export const isReIssueRequired = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state?.isReIssueRequired
);

export const selectNeedsDocsa = createSelector(
  selectInvoluntaryChange,
  (state) => state.needsDocsaRequest
);

export const selectDocsaDocs = createSelector(selectInvoluntaryChange, (state) => state.docsaDocs);

export const selectOriginalItinerary = createSelector(
  selectInvoluntaryChange,
  (state) => state.originalItinerary
);

export const selectNewPassengers = createSelector(selectInvoluntaryChange, (state) => {
  return state.newPnr.passengers.map((pass) => ({
    ticketNumber: pass.ticket.number,
    passengerType: pass.type
  }));
});

export const selectNewPassengersDocsa = createSelector(selectInvoluntaryChange, (state) => {
  return state.newPnr
    ? state.newPnr.passengers.map((pass) => ({
        ticketNumber: pass.ticket.number,
        passengerType: pass.type
      }))
    : state.involuntaryQuote?.passenger.map((pass) => ({
        ticketNumber: pass.ticket.number,
        passengerType: pass.type
      }));
});

export const selectDocsaRequest = createSelector(
  selectInvoluntaryChange,
  selectQuoteRequest,
  selectNewPassengersDocsa,
  (state, quote, passengers) => {
    return {
      pnr: state.newPnr ? state.newPnr.code : quote.pnr,
      country: quote.country,
      gds: quote.gds,
      docs: state.docsaDocs,
      passengers,
      flights: quote.flights
    } as DOCSARequest;
  }
);

export const selectExchangePricing = createSelector(
  selectInvoluntaryChange,
  (state) => state.exchangePricing
);

export const getFareFamiliesState = createSelector(
  selectInvoluntaryChange,
  (state) => state.fareFamiliesState
);

export const getExchangeAvailability = createSelector(
  selectInvoluntaryChange,
  (state) => state.availabilitiesPerSegment
);

export const selectExchangeFareOverrideRequest = createSelector(
  selectInvoluntaryChange,
  (state) => ({
    ...state.quoteRequest,
    selectedOption: null,
    exchangeSegments: null,
    originalFare: !!state.exchangePricing.exchangeFare ? state.exchangePricing.exchangeFare : null,
    exchangeFare: !!state.exchangePricing.exchangeFare ? state.exchangePricing.exchangeFare : null
  })
);

export const selectExchangeOverrideState = createSelector(selectInvoluntaryChange, (state) => {
  return {
    ...state.exchangeOverrideState,
    canBook: true
  };
});

export const selectSomePassengerHasAncilliary = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) =>
    state.involuntaryQuote.passenger.some((passenger) =>
      passenger.specialRequests.some((specialRequest) => specialRequest.coupons.length > 0)
    )
);

export const getExchangeOriginalRoute = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.exchangeOriginalRoute
);

export const getShowAncillariesModal = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.showAncillariesModal
);

export const getAncillariesError = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.ancillariesError
);

export const getPassengersWithAncillaryValidation = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.passengersWithAncillaryValidation
);

export const getDataToCheckStep = createSelector(
  selectInvoluntaryChange,
  selectPnrState,
  selectSettingsState,
  (state, pnrState, settingsState) => {
    return {
      options: state.availabilitiesPerSegment,
      isAirline: settingsState.clientSettings.isAirline,
      settings: settingsState.clientSettings,
      isDocsRequired: pnrState.pnrInfo.isDocsRequired
    };
  }
);

export const getAncillariesWaiveState = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.ancillariesWaiveState
);

export const getInvoluntaryBookStepStates = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.involuntaryBookStepStates
);

export const getReloadAvailabilities = createSelector(
  selectInvoluntaryChange,
  (state: InvoluntaryChangeState) => state.reloadAvailabilities
);
